/**
 * The post-season chapter's translation between the pinned client and the §8 components.
 *
 * `12` §2.4 control 2 says `bata-ui` never hand-rolls a request. It does not say the
 * API's response models and the design system's component contracts are the same types —
 * they are not, and the difference is where the honest work is. Every mapping below is
 * one of three things and each is labelled:
 *
 *   TRANSFORM  a shape change that loses nothing
 *   DERIVE     a value computed from what the contract does carry, stated as such
 *   GAP        a field a screen needs that the contract does not expose
 *
 * The GAPs are not filled with plausible numbers. Where the contract is silent the screen
 * renders `null` with a reason key and says why (`docs/adr/0014`, `docs/adr/0070`); a
 * zero on a recovery residual or a defect rate would be read as evidence.
 */

import type {
  AccuracyRow,
  ControlTotalsResponse,
  DemandPanelResponse,
  RecoveryRunRow,
  SupplierScorecardRow,
} from '@/api/generated';
import type { StatusLevel } from '@/primitives';

// ── the demand basis: the field the whole chapter turns on ───────────────────

/**
 * The two bases a demand figure can be on. Published by the recovery reads as
 * `demandBasis`, and it is the difference between a number a buy may be fitted to and a
 * number that will teach the next buy to under-buy exactly the sizes that sold out.
 *
 * reqs/08 §7: "a size curve fitted to raw sales history simply relearns which sizes were
 * out of stock". `06.P2` exists to prevent that reading, and it can only do so if the
 * basis travels with the figure to the pixel — which is why the screens render it beside
 * the number rather than in a tooltip or a metadata strip.
 */
export const UNCONSTRAINED_DEMAND = 'UNCONSTRAINED_DEMAND';
export const CENSORED_SALES = 'CENSORED_SALES';

export type BasisVerdict = 'recovered' | 'censored' | 'unrecognised';

/**
 * DERIVE — the verdict from the published string.
 *
 * A basis this build does not recognise is `unrecognised` and NOT `recovered`. The two
 * directions are not symmetric: treating an unknown basis as recovered demand licenses a
 * size fit on whatever the string turned out to mean, and the failure is silent and lands
 * a season later in the buy. Treating it as unrecognised costs a planner one question.
 */
export function basisVerdict(demandBasis: string): BasisVerdict {
  if (demandBasis === UNCONSTRAINED_DEMAND) return 'recovered';
  if (demandBasis === CENSORED_SALES) return 'censored';
  return 'unrecognised';
}

/** The catalogue key naming the basis, for the label that sits with the number. */
export function basisLabelKey(demandBasis: string): string {
  const verdict = basisVerdict(demandBasis);
  if (verdict === 'recovered') return 'postseason.basis.unconstrainedDemand';
  if (verdict === 'censored') return 'postseason.basis.censoredSales';
  return 'postseason.basis.unrecognised';
}

/** §12.6 — a status is never colour alone, and the level drives the icon. */
export function basisStatusLevel(demandBasis: string): StatusLevel {
  const verdict = basisVerdict(demandBasis);
  if (verdict === 'recovered') return 'good';
  if (verdict === 'censored') return 'critical';
  return 'serious';
}

/**
 * DERIVE — is the panel fit to fit a size curve on?
 *
 * Two conditions, both published, and both have to hold: the basis is recovered demand,
 * and no week in the panel went unrecovered. A panel on recovered demand with two hundred
 * unrecovered weeks is a panel with a hole in it, and the census is exactly what `12` §5
 * M6's entry gate reads.
 */
export function panelIsFitToFit(panel: DemandPanelResponse): boolean {
  return basisVerdict(panel.demandBasis) === 'recovered'
    && panel.weeksWithoutUnconstrainedDemand === 0;
}

// ── recovery control totals ──────────────────────────────────────────────────

/**
 * TRANSFORM — the control-total map into a lookup that returns `null` rather than `0`.
 *
 * `ControlTotalsResponse.totals` is an open map, so a measure the run did not persist is
 * ABSENT from it. `totals[key] ?? 0` would turn "this run recorded no recovered units"
 * into "this run recovered nothing", and those are opposite findings on a screen whose
 * whole job is the difference. `undefined` becomes `null` and the screen says why.
 */
export function controlTotal(
  totals: ControlTotalsResponse | undefined,
  measure: string,
): number | null {
  const value = totals?.totals[measure];
  return typeof value === 'number' ? value : null;
}

/** The measure names the control-total map is keyed by, per ADR 0091 D3. */
export const OBSERVED_UNITS = 'observed_units';
export const RECOVERED_UNITS = 'recovered_units';
export const UNCONSTRAINED_UNITS = 'unconstrained_demand_units';

// ── the accuracy warehouse ───────────────────────────────────────────────────

/**
 * GAP — **there is no planner dimension, anywhere in the estate.**
 *
 * `12` §5 M5 names "accuracy and bias by planner, category and horizon" as a milestone
 * measure. `AccuracyRow` carries `dimensionKey`, `observations` and the four metrics, and
 * nothing else; the operation's own description dimensions the warehouse "by grain,
 * horizon, model version and owner", and `owner` there is the OWNER OF A MODEL — the team
 * accountable for `fc-v3` — not the planner who forecast a category.
 *
 * So accuracy by planner is not renderable, and the screen says so rather than relabelling
 * `owner` as "planner" to fill the column. A planner-labelled model owner would answer a
 * question nobody has the data for, and it would be believed: the whole point of the M5
 * measure is to find the planner whose overrides make the forecast worse, and a screen
 * that appeared to answer it with the model owner's name would send that conversation to
 * the wrong person. `docs/adr/0071` records the refusal.
 *
 * The second, smaller gap: `dimensionKey` is an ORDERED TUPLE OF OPAQUE STRINGS. The
 * contract publishes neither the names of its positions nor their count, so the screen
 * renders the segments as the composite key they are and cannot label position 2 "horizon"
 * — which would be a guess about a contract that does not promise an order.
 */
export function accuracyDimensionSegments(row: AccuracyRow): readonly string[] {
  return row.dimensionKey.filter((segment) => segment !== '');
}

/** The separator between segments of a composite key. Punctuation, not copy. */
const SEGMENT_SEPARATOR = ' · ';

/** DERIVE — a chart category label from the composite key. Data, never a sentence. */
export function accuracyCategoryLabel(row: AccuracyRow): string {
  const segments = accuracyDimensionSegments(row);
  return segments.length === 0 ? row.observations.toFixed(0) : segments.join(SEGMENT_SEPARATOR);
}

/**
 * DERIVE — whether the seasonal-naive baseline won.
 *
 * reqs/08 §5: "seasonal-naive frequently wins today — that is the bar." MASE at or above
 * 1.0 means it won here — the platform's own rule (`planning/postseason/accuracy.py`
 * `mase`: "at or above one, seasonal-naive wins"); at exactly 1 the model added no skill.
 * `null` is not a win and not a loss: it is no comparison, and the three are three.
 */
export function naiveBaselineWon(row: AccuracyRow): boolean | null {
  return row.mase === null ? null : row.mase >= 1;
}

// ── supplier scorecards ──────────────────────────────────────────────────────

/**
 * DERIVE — the ranking rule, pure and total.
 *
 * Unscored last, then composite descending, then supplier key. A supplier nobody
 * inspected has a `null` composite, and sorting `null` as `0` would put them at the
 * bottom with the genuine failures while sorting it as "missing means fine" would put
 * them at the top with an implied perfect score. Neither is true, so they rank last and
 * visibly unmeasured, and a sourcing decision cannot be made on them by accident.
 */
export function rankSuppliers(
  rows: readonly SupplierScorecardRow[],
): readonly SupplierScorecardRow[] {
  return [...rows].sort((left, right) => {
    if ((left.compositeScore === null) !== (right.compositeScore === null)) {
      return left.compositeScore === null ? 1 : -1;
    }
    const delta = (right.compositeScore ?? 0) - (left.compositeScore ?? 0);
    return delta !== 0 ? delta : left.supplierKey.localeCompare(right.supplierKey);
  });
}

// ── governance vocabularies ──────────────────────────────────────────────────

/**
 * DERIVE — a service reason code into a catalogue key.
 *
 * The adoption states and promotion outcomes are the platform's vocabulary and this is
 * the front end's copy of it. A code this build does not name renders as a LABELLED
 * unrecognised state with the raw code beside it, rather than as `⟦postseason.outcome.x⟧`
 * — a planner seeing bracket noise learns the screen is broken, and a planner seeing a
 * blank learns nothing at all. Both are worse than being told the outcome is one this
 * release does not know, which is a true statement and an actionable one.
 *
 * Adding a code to the service means adding its message in all three catalogues. That is
 * the same contract `api.problem.<code>` has, for the same reason.
 */
const ADOPTION_STATES = new Set([
  'adoptable', 'unsigned', 'mid_plan_adoption_refused', 'no_proposal',
]);

export const UNRECOGNISED_ADOPTION_KEY = 'postseason.adoption.unrecognised';

export function adoptionLabelKey(adoptionState: string): string {
  return ADOPTION_STATES.has(adoptionState)
    ? `postseason.adoption.${adoptionState}`
    : UNRECOGNISED_ADOPTION_KEY;
}

const OUTCOME_CODES = new Set([
  'promoted',
  'insufficient_consecutive_windows',
  'did_not_beat_champion_on_primary_metric',
  'bias_worsened',
  'evaluated_at_wrong_grain',
  'attribute_forecast_beats_baseline',
  'attribute_forecast_no_better_than_baseline',
  'no_held_out_new_options',
  'held_out_actuals_are_zero',
  'held_out_options_have_no_forecast',
]);

export const UNRECOGNISED_OUTCOME_KEY = 'postseason.outcome.unrecognised';

export function outcomeLabelKey(outcomeCode: string): string {
  return OUTCOME_CODES.has(outcomeCode)
    ? `postseason.outcome.${outcomeCode}`
    : UNRECOGNISED_OUTCOME_KEY;
}

/**
 * DERIVE — an attribute name into its catalogue key.
 *
 * 13 §4.3 registers the attribute vocabulary and the platform publishes the member names.
 * An attribute this release does not name renders under a labelled unrecognised key with
 * the raw name shown beside it, for the same reason as an outcome code: a registered
 * attribute added upstream should read as "new", not as bracket noise.
 */
const ATTRIBUTE_NAMES = new Set([
  'colour_family', 'material_upper', 'material_sole', 'silhouette', 'price_band',
]);

export const UNRECOGNISED_ATTRIBUTE_KEY = 'postseason.attribute.unrecognised';

export function attributeLabelKey(attribute: string): string {
  return ATTRIBUTE_NAMES.has(attribute)
    ? `postseason.attribute.${attribute}`
    : UNRECOGNISED_ATTRIBUTE_KEY;
}

// ── recovery runs ────────────────────────────────────────────────────────────

/**
 * DERIVE — the newest run for a season from the run list.
 *
 * `GET /postseason/recovery/latest` answers this for a season directly and is what the
 * screens use. This is the list-side equivalent, used where a screen already holds the
 * rows and a second round trip would tell it nothing new. The list is not promised in any
 * order, so "newest" is taken as the last row for the season rather than assumed to be
 * the first — an assumption about ordering the contract does not make is a bug that only
 * appears once the store has two runs.
 */
export function runsForSeason(
  rows: readonly RecoveryRunRow[],
  season: string,
): readonly RecoveryRunRow[] {
  return rows.filter((row) => row.season === season);
}

/**
 * DERIVE — the census of censored cells that a run left unrecovered.
 *
 * `cellsCensored` and `cellsModelled` are both published. A run that modelled fewer cells
 * than it censored has left the difference unrecovered, and that difference is the number
 * `12` §5 M6's entry criterion reads. Where the two are equal the answer is zero and that
 * zero is a MEASUREMENT, which is why it is returned as a number rather than as an
 * absence — unlike every other zero in this chapter, this one was computed.
 */
export function unrecoveredCells(row: RecoveryRunRow): number {
  return Math.max(0, row.cellsCensored - row.cellsModelled);
}
